import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Box, Snackbar, Alert } from '@mui/material';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { styled } from '@mui/material/styles';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import ThemeBackground from '../../components/Layout/ThemeBackground';
import InductionManagement from '../../components/admin/InductionManagement';
import ApplicationManagement from '../../components/admin/ApplicationManagement';
import InterviewManagement from '../Admin/InterviewManagement';
import OnboardingManagement from '../../components/admin/OnboardingManagement';
import GroupSupervisionManagement from '../../components/admin/GroupSupervisionManagement';

// Add global styles for DatePicker to ensure visibility in modals
const globalDatePickerStyles = `
  .react-datepicker-popper {
    z-index: 99999 !important;
    position: fixed !important;
  }
  .react-datepicker {
    z-index: 99999 !important;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3) !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = globalDatePickerStyles;
  if (!document.head.querySelector('style[data-datepicker-modal]')) {
    styleElement.setAttribute('data-datepicker-modal', 'true');
    document.head.appendChild(styleElement);
  }
}

const DatePickerWrapper = styled('div')(({ theme }) => ({
  '& .react-datepicker-popper': {
    zIndex: 99999,
    position: 'fixed !important',
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '8px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    fontSize: '14px',
    zIndex: 99999,
    '& .react-datepicker__header': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      borderBottom: 'none',
      borderRadius: '8px 8px 0 0',
      padding: '8px 0',
    },
    '& .react-datepicker__current-month': {
      color: 'white',
      fontWeight: 'bold',
      fontSize: '16px',
    },
    '& .react-datepicker__day-name': {
      color: 'white',
      fontSize: '12px',
      fontWeight: 'bold',
    },
    '& .react-datepicker__navigation': {
      top: '12px',
      '&--previous': {
        borderRightColor: 'white',
      },
      '&--next': {
        borderLeftColor: 'white',
      },
    },
    '& .react-datepicker__day': {
      fontSize: '14px',
      margin: '2px',
      width: '32px',
      height: '32px',
      lineHeight: '32px',
      '&:hover': {
        backgroundColor: theme.palette.primary.light,
        color: 'white',
        borderRadius: '4px',
      },
    },
    '& .react-datepicker__day--selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      borderRadius: '4px',
      fontWeight: 'bold',
    },
    '& .react-datepicker__day--keyboard-selected': {
      backgroundColor: theme.palette.primary.light,
      color: 'white',
      borderRadius: '4px',
    },
    '& .react-datepicker__day--today': {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.primary,
      fontWeight: 'bold',
      borderRadius: '4px',
    },
    '& .react-datepicker__month': {
      padding: '8px',
    },
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '12px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.9)',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)'
    },
    '&:focus': {
      outline: 'none',
      borderColor: 'rgba(255, 255, 255, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.1)'
    }
  }
}));

interface Counsellor {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  dob: Date;
  applicationStatus: string;
  applicationToken?: string;
  applicationExpiryDate?: Date | string;
  createdAt: string;
  updatedAt: string;
  underrepresentedCategories?: string[]; // Desirables from interest registration
  application?: {
    counselling?: {
      experienceOrTraining?: string[];
      experience?: string;
      previousExperience?: string;
    };
  };
}

const CounsellorManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [counsellors, setCounsellors] = useState<Counsellor[]>([]);
  const [filteredCounsellors, setFilteredCounsellors] = useState<Counsellor[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedCounsellorId, setSelectedCounsellorId] = useState<string | null>(null);
  const [selectedCounsellorName, setSelectedCounsellorName] = useState<string>('');
  const [applicationExpiryDate, setApplicationExpiryDate] = useState<Date | null>(null);
  const [removeReason, setRemoveReason] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCounsellors, setSelectedCounsellors] = useState<Set<string>>(new Set());
  const [showBulkIssueModal, setShowBulkIssueModal] = useState(false);
  const [showBulkRemoveModal, setShowBulkRemoveModal] = useState(false);
  const [bulkExpiryDate, setBulkExpiryDate] = useState<Date | null>(null);
  const [bulkRemoveReason, setBulkRemoveReason] = useState<string>('');
  const itemsPerPage = 10;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const handleSuccess = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 5000);
  };

  const handleError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  const fetchCounsellors = async () => {
    try {
      const response = await api.get('/api/counsellors');
      console.log('Counsellors response:', response.data);
      
      if (response.data) {
        // Filter to show only registered-interest applicants awaiting
        // screening (applied via website or added without issuance; not yet
        // issued an application - no token/expiry).
        const pendingApplicants = response.data
          .filter((counsellor: Counsellor) =>
            counsellor !== null &&
            ['Registered Interest', 'Pending'].includes(counsellor.applicationStatus) &&
            // Not yet issued an application (no token/expiry set)
            !counsellor.applicationExpiryDate &&
            !counsellor.applicationToken
          )
          .sort((a: Counsellor, b: Counsellor) => {
            const dateA = new Date(a.createdAt || 0).getTime();
            const dateB = new Date(b.createdAt || 0).getTime();
            return dateB - dateA;
          });
        setCounsellors(pendingApplicants);
        setFilteredCounsellors(pendingApplicants);
      }
    } catch (error: any) {
      console.error('Error fetching counsellors:', error);
      setError('Failed to fetch applicants');
      setCounsellors([]);
      setFilteredCounsellors([]);
    }
  };

  // Initial load with loading state
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchCounsellors();
      setLoading(false);
    };
    loadInitialData();
  }, []);

  // Auto-refresh every 30 seconds (silent, no loading state, only on Applicant Screening tab)
  useEffect(() => {
    if (activeTab !== 0) return;

    const refreshInterval = setInterval(() => {
      fetchCounsellors();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [activeTab]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = counsellors.filter(
      (counsellor) =>
        `${counsellor.firstName} ${counsellor.lastName}`.toLowerCase().includes(query) ||
        counsellor.email.toLowerCase().includes(query) ||
        counsellor.phone.toLowerCase().includes(query)
    );
    setFilteredCounsellors(filtered);
    setCurrentPage(1);
  };


  const formatDate = (dateString: string | Date) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const calculateAge = (dateString: string | Date) => {
    if (!dateString) return null;
    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const getExperienceAndTraining = (counsellor: Counsellor) => {
    const experienceOrTraining = counsellor.application?.counselling?.experienceOrTraining || [];
    const experience = counsellor.application?.counselling?.experience || '';
    const previousExperience = counsellor.application?.counselling?.previousExperience || '';
    
    const allExperience = [...experienceOrTraining];
    if (experience) allExperience.push(experience);
    if (previousExperience) allExperience.push(previousExperience);
    
    return allExperience.length > 0 ? allExperience.join(', ') : 'Not specified';
  };

  const getDesirables = (counsellor: Counsellor) => {
    // Display underrepresented categories (Desirables) from interest registration
    if (counsellor.underrepresentedCategories && counsellor.underrepresentedCategories.length > 0) {
      return counsellor.underrepresentedCategories.join(', ');
    }
    return 'None specified';
  };

  // Pagination logic
  const totalItems = filteredCounsellors.length;
  const totalPages = Math.max(Math.ceil(totalItems / itemsPerPage), 1);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCounsellors.slice(indexOfFirstItem, indexOfLastItem);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleIssueApplication = (counsellorId: string) => {
    setSelectedCounsellorId(counsellorId);
    setApplicationExpiryDate(null);
    setShowExpiryModal(true);
  };

  const processApplicationIssue = async () => {
    if (!selectedCounsellorId || !applicationExpiryDate) {
      setSnackbar({
        open: true,
        message: 'Please select an expiry date',
        severity: 'warning'
      });
      return;
    }

    try {
      setIsProcessing(true);
      
      // Set time to 23:59:59 on the selected date
      const expiryDateWithTime = new Date(applicationExpiryDate);
      expiryDateWithTime.setHours(23, 59, 59, 999);
      
      // Issue the application via the dedicated endpoint (token + expiry +
      // status transition + invitation email all handled server-side)
      await api.post(`/api/counsellors/${selectedCounsellorId}/issue-application`, {
        expiryDate: expiryDateWithTime,
      });
      
      // Close modal and refresh list
      setShowExpiryModal(false);
      setSelectedCounsellorId(null);
      setApplicationExpiryDate(null);
      fetchCounsellors();
      
      setSnackbar({
        open: true,
        message: 'Application issued successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error issuing application:', error);
      setSnackbar({
        open: true,
        message: 'Error issuing application. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    if (!isProcessing) {
      setShowExpiryModal(false);
      setSelectedCounsellorId(null);
      setApplicationExpiryDate(null);
    }
  };

  const handleRemoveApplication = (counsellorId: string) => {
    const counsellor = counsellors.find(c => c._id === counsellorId);
    if (counsellor) {
      setSelectedCounsellorId(counsellorId);
      setSelectedCounsellorName(`${counsellor.firstName} ${counsellor.lastName}`);
      setRemoveReason('');
      setShowRemoveModal(true);
    }
  };

  const processApplicationRemoval = async () => {
    if (!selectedCounsellorId || !removeReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a reason for removing this application',
        severity: 'warning'
      });
      return;
    }

    try {
      setIsProcessing(true);
      
      // Delete the counsellor application with reason
      await api.delete(`/api/counsellors/${selectedCounsellorId}`, {
        data: { reason: removeReason.trim() }
      });
      
      // Close modal and refresh list
      setShowRemoveModal(false);
      setSelectedCounsellorId(null);
      setSelectedCounsellorName('');
      setRemoveReason('');
      fetchCounsellors();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Application removed successfully and candidate has been notified by email.',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error removing application:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error removing application. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseRemoveModal = () => {
    if (!isProcessing) {
      setShowRemoveModal(false);
      setSelectedCounsellorId(null);
      setSelectedCounsellorName('');
      setRemoveReason('');
    }
  };

  // Bulk action handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(currentItems.map(c => c._id));
      setSelectedCounsellors(allIds);
    } else {
      setSelectedCounsellors(new Set());
    }
  };

  const handleSelectCounsellor = (counsellorId: string, checked: boolean) => {
    const newSelected = new Set(selectedCounsellors);
    if (checked) {
      newSelected.add(counsellorId);
    } else {
      newSelected.delete(counsellorId);
    }
    setSelectedCounsellors(newSelected);
  };

  const handleBulkIssue = () => {
    if (selectedCounsellors.size === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one applicant',
        severity: 'warning'
      });
      return;
    }
    setBulkExpiryDate(null);
    setShowBulkIssueModal(true);
  };

  const handleBulkRemove = () => {
    if (selectedCounsellors.size === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one applicant',
        severity: 'warning'
      });
      return;
    }
    setBulkRemoveReason('');
    setShowBulkRemoveModal(true);
  };

  const processBulkIssue = async () => {
    if (!bulkExpiryDate) {
      setSnackbar({
        open: true,
        message: 'Please select an expiry date',
        severity: 'warning'
      });
      return;
    }

    try {
      setIsProcessing(true);
      
      // Set time to 23:59:59 on the selected date
      const expiryDateWithTime = new Date(bulkExpiryDate);
      expiryDateWithTime.setHours(23, 59, 59, 999);
      
      // Issue applications for all selected counsellors
      const promises = Array.from(selectedCounsellors).map(counsellorId =>
        api.post(`/api/counsellors/${counsellorId}/issue-application`, {
          expiryDate: expiryDateWithTime,
        })
      );
      
      await Promise.all(promises);
      
      setShowBulkIssueModal(false);
      setBulkExpiryDate(null);
      setSelectedCounsellors(new Set());
      fetchCounsellors();
      
      setSnackbar({
        open: true,
        message: `Successfully issued ${promises.length} application(s)`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error issuing bulk applications:', error);
      setSnackbar({
        open: true,
        message: 'Error issuing applications. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const processBulkRemove = async () => {
    if (!bulkRemoveReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a reason for removal',
        severity: 'warning'
      });
      return;
    }

    try {
      setIsProcessing(true);
      
      // Remove all selected counsellors
      const promises = Array.from(selectedCounsellors).map(counsellorId =>
        api.delete(`/api/counsellors/${counsellorId}`, {
          data: { reason: bulkRemoveReason.trim() }
        })
      );
      
      await Promise.all(promises);
      
      setShowBulkRemoveModal(false);
      setBulkRemoveReason('');
      setSelectedCounsellors(new Set());
      fetchCounsellors();
      
      setSnackbar({
        open: true,
        message: `Successfully removed ${promises.length} application(s) and notified candidates`,
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error removing bulk applications:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Error removing applications. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8">
          <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
            <div className="flex justify-center items-center h-64">
              <Typography variant="h6" className="theme-text-primary">
                Loading applicants...
              </Typography>
            </div>
          </div>
        </div>
      </ThemeBackground>
    );
  }

  if (error) {
    return (
      <ThemeBackground variant="full">
        <div className="container mx-auto px-6 py-8">
          <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
            <div className="flex justify-center items-center h-64">
              <Typography variant="h6" className="theme-text-primary" color="error">
                {error}
              </Typography>
            </div>
          </div>
        </div>
      </ThemeBackground>
    );
  }

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8 overflow-y-scroll">
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-6">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-start">
                  {/* Buttons on the left */}
                  <div className="flex flex-col space-y-4">
                    <div className="flex space-x-4">
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: '160px',
                          backgroundColor: '#2196f3',
                          '&:hover': {
                            backgroundColor: '#1976d2'
                          }
                        }}
                        onClick={() => navigate('/')}
                      >
                        Back to Home
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: '160px',
                          backgroundColor: '#4caf50',
                          '&:hover': {
                            backgroundColor: '#388e3c'
                          }
                        }}
                        onClick={() => navigate('/counsellor-create')}
                      >
                        Invite Counsellor
                      </Button>
                    </div>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                      Use "Invite Counsellor" for new applicants or existing employees. Toggle "Existing Employee" for self-service onboarding.
                    </Typography>
                  </div>

                  {/* Title and user info on the right */}
                  <div className="text-right">
                    <Typography 
                      variant="h3" 
                      component="h1" 
                      gutterBottom
                      className="theme-text-primary"
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 800,
                        fontSize: '2.5rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Counsellor Management
                    </Typography>
                    <Typography 
                      variant="subtitle1" 
                      className="theme-text-secondary"
                      sx={{
                        fontWeight: 500,
                        fontSize: '1.1rem'
                      }}
                    >
                      {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Admin User' : 'Admin User'}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* Success/Error Messages */}
            {success && (
              <div className="mb-6 p-4 glass-card-light border-l-4 border-green-400">
                <Typography variant="body1" className="text-green-600 font-semibold">
                  {success}
                </Typography>
              </div>
            )}
            {error && (
              <div className="mb-6 p-4 glass-card-light border-l-4 border-red-400">
                <Typography variant="body1" className="text-red-600 font-semibold">
                  {error}
                </Typography>
              </div>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    minWidth: 180
                  },
                  '& .Mui-selected': {
                    color: 'rgba(255, 255, 255, 1)'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#64b5f6'
                  }
                }}
              >
                <Tab label="Applicant Screening" />
                <Tab label="Application Management" />
                <Tab label="Interview Management" />
                <Tab label="Onboarding" />
                <Tab label="Induction Management" />
                <Tab label="Group Supervision" />
              </Tabs>
            </Box>

            {/* Applicant Screening Tab */}
            {activeTab === 0 && (
              <>
                {/* Info Banner */}
                <div className="mb-6 p-4 glass-card-light border-l-4 border-blue-400">
                  <Typography variant="body1" className="theme-text-primary mb-2 font-semibold">
                    Applicant Screening Dashboard
                  </Typography>
                  <Typography variant="body2" className="theme-text-secondary">
                    Review counsellor applications from the website. Use "Issue Application" to approve and move to the main counsellor system, or "Remove Application" to decline.
                  </Typography>
                </div>

            {/* Search Bar and Bulk Actions */}
            <div className="mb-6 space-y-4">
              <input
                type="text"
                placeholder="Search applicants by name, email, or phone..."
                value={searchQuery}
                onChange={handleSearch}
                className="theme-input w-full"
              />
              
              {/* Bulk Actions Bar */}
              {selectedCounsellors.size > 0 && (
                <div className="flex items-center justify-between p-4 glass-card-light border-l-4 border-indigo-400">
                  <Typography variant="body2" className="theme-text-primary font-semibold">
                    {selectedCounsellors.size} applicant(s) selected
                  </Typography>
                  <div className="flex space-x-3">
                    <Button
                      variant="contained"
                      onClick={handleBulkIssue}
                      disabled={isProcessing}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        '&:hover': {
                          backgroundColor: 'rgba(76, 175, 80, 0.3)',
                        },
                      }}
                    >
                      Bulk Issue Applications
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleBulkRemove}
                      disabled={isProcessing}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                        color: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 0.3)',
                        },
                      }}
                    >
                      Bulk Remove Applications
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setSelectedCounsellors(new Set())}
                      disabled={isProcessing}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.9)',
                        borderColor: 'rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            <div className="overflow-x-auto glass-card">
              <table className="min-w-full">
                <thead className="glass-card-light">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={currentItems.length > 0 && currentItems.every(c => selectedCounsellors.has(c._id))}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      First Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Last Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Telephone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Date of Birth
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Experience & Training
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Desirables
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Application Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold theme-text-secondary uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {currentItems.map((counsellor) => (
                    <tr key={counsellor._id} className="hover:bg-white/5 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedCounsellors.has(counsellor._id)}
                          onChange={(e) => handleSelectCounsellor(counsellor._id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium theme-text-primary">
                          {counsellor.firstName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium theme-text-primary">
                          {counsellor.lastName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm theme-text-muted">
                          {counsellor.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm theme-text-muted">
                          {counsellor.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm theme-text-muted">
                          {formatDate(counsellor.dob)}
                          {counsellor.dob && (
                            <span className="ml-2 text-xs theme-text-secondary">
                              (Age: {calculateAge(counsellor.dob)})
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm theme-text-muted max-w-xs truncate" title={getExperienceAndTraining(counsellor)}>
                          {getExperienceAndTraining(counsellor)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm theme-text-muted">
                          {getDesirables(counsellor)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm theme-text-muted">
                          {formatDate(counsellor.createdAt)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleIssueApplication(counsellor._id);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200"
                          >
                            Issue Application
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveApplication(counsellor._id);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1 rounded transition-colors duration-200"
                          >
                            Remove Application
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
              <div className="flex-1 flex justify-between sm:hidden space-x-2">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-all duration-200 ${
                    currentPage === 1
                      ? 'border-white/20 theme-text-muted cursor-not-allowed'
                      : 'theme-button-secondary'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md transition-all duration-200 ${
                    currentPage === totalPages
                      ? 'border-white/20 theme-text-muted cursor-not-allowed'
                      : 'theme-button-secondary'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm theme-text-secondary">
                    Showing{' '}
                    <span className="font-medium theme-text-primary">{indexOfFirstItem + 1}</span>
                    {' - '}
                    <span className="font-medium theme-text-primary">
                      {Math.min(indexOfLastItem, totalItems)}
                    </span>{' '}
                    of <span className="font-medium theme-text-primary">{totalItems}</span> pending applications
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex space-x-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-3 py-2 rounded-md border text-sm font-medium transition-all duration-200 ${
                        currentPage === 1
                          ? 'border-white/20 theme-text-muted cursor-not-allowed glass-card-light'
                          : 'theme-button-secondary'
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-3 py-2 rounded-md border text-sm font-medium transition-all duration-200 ${
                        currentPage === totalPages
                          ? 'border-white/20 theme-text-muted cursor-not-allowed glass-card-light'
                          : 'theme-button-secondary'
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
              </>
            )}

            {/* Application Management Tab */}
            {activeTab === 1 && (
              <ApplicationManagement onSuccess={handleSuccess} onError={handleError} />
            )}

            {/* Interview Management Tab */}
            {activeTab === 2 && (
              <InterviewManagement />
            )}

            {/* Onboarding Management Tab */}
            {activeTab === 3 && (
              <OnboardingManagement />
            )}

            {/* Induction Management Tab */}
            {activeTab === 4 && (
              <InductionManagement onSuccess={handleSuccess} onError={handleError} />
            )}

            {/* Group Supervision Tab */}
            {activeTab === 5 && (
              <GroupSupervisionManagement />
            )}
          </div>
        </div>
      </div>

      {/* Application Expiry Date Modal */}
      <Dialog 
        open={showExpiryModal} 
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 1300,
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            zIndex: 1300,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Issue Application
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
            Please set the application expiry date for this counsellor:
          </Typography>
          <div className="mb-4">
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
              Application Expiry Date *
            </Typography>
            <DatePickerWrapper>
              <DatePicker
                selected={applicationExpiryDate}
                onChange={(date: Date | null) => setApplicationExpiryDate(date)}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select expiry date"
                shouldCloseOnSelect
                showPopperArrow={false}
                popperPlacement="bottom-start"
                portalId="root"
              />
            </DatePickerWrapper>
          </div>
          {applicationExpiryDate && (
            <Typography variant="body2" sx={{ color: 'rgba(76, 175, 80, 0.9)', mt: 1 }}>
              Application will expire on: {applicationExpiryDate.toLocaleDateString('en-GB')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseModal} 
            disabled={isProcessing}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={processApplicationIssue}
            variant="contained"
            disabled={!applicationExpiryDate || isProcessing}
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.3)',
                borderColor: 'rgba(76, 175, 80, 0.5)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isProcessing ? 'Processing...' : 'Issue Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Application Modal */}
      <Dialog 
        open={showRemoveModal} 
        onClose={handleCloseRemoveModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Remove Application
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
            Are you sure you want to remove the application for <strong>{selectedCounsellorName}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'rgba(244, 67, 54, 0.9)' }}>
            This action cannot be undone. The candidate will be notified by email that their application has been rejected.
          </Typography>
          <div className="mb-4">
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
              Reason for removal *
            </Typography>
            <textarea
              value={removeReason}
              onChange={(e) => setRemoveReason(e.target.value)}
              placeholder="Please provide a reason for removing this application..."
              className="w-full p-3 border rounded-md resize-none"
              rows={4}
              style={{
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            />
          </div>
          {removeReason.trim() && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1, fontStyle: 'italic' }}>
              This reason is for internal reference only and will not be sent to the candidate.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseRemoveModal} 
            disabled={isProcessing}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={processApplicationRemoval}
            variant="contained"
            disabled={!removeReason.trim() || isProcessing}
            sx={{
              backgroundColor: 'rgba(244, 67, 54, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.3)',
                borderColor: 'rgba(244, 67, 54, 0.5)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isProcessing ? 'Removing...' : 'Remove Application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Issue Applications Modal */}
      <Dialog 
        open={showBulkIssueModal} 
        onClose={() => !isProcessing && setShowBulkIssueModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Bulk Issue Applications
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
            Issue applications for {selectedCounsellors.size} selected applicant(s)
          </Typography>
          <div className="mb-4">
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
              Application Expiry Date *
            </Typography>
            <DatePickerWrapper>
              <DatePicker
                selected={bulkExpiryDate}
                onChange={(date: Date | null) => setBulkExpiryDate(date)}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select expiry date"
                shouldCloseOnSelect
                showPopperArrow={false}
                popperPlacement="bottom-start"
                portalId="root"
              />
            </DatePickerWrapper>
          </div>
          {bulkExpiryDate && (
            <Typography variant="body2" sx={{ color: 'rgba(76, 175, 80, 0.9)', mt: 1 }}>
              Applications will expire at 23:59 on: {bulkExpiryDate.toLocaleDateString('en-GB')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setShowBulkIssueModal(false)} 
            disabled={isProcessing}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={processBulkIssue}
            variant="contained"
            disabled={!bulkExpiryDate || isProcessing}
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(76, 175, 80, 0.3)',
                borderColor: 'rgba(76, 175, 80, 0.5)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isProcessing ? 'Processing...' : `Issue ${selectedCounsellors.size} Application(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Remove Applications Modal */}
      <Dialog 
        open={showBulkRemoveModal} 
        onClose={() => !isProcessing && setShowBulkRemoveModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)' }}>
          Bulk Remove Applications
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255, 255, 255, 0.9)' }}>
            Remove {selectedCounsellors.size} selected application(s)?
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'rgba(244, 67, 54, 0.9)' }}>
            This action cannot be undone. All selected candidates will be notified by email that their applications have been rejected.
          </Typography>
          <div className="mb-4">
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium', color: 'rgba(255, 255, 255, 0.9)' }}>
              Reason for removal *
            </Typography>
            <textarea
              value={bulkRemoveReason}
              onChange={(e) => setBulkRemoveReason(e.target.value)}
              placeholder="Please provide a reason for removing these applications..."
              className="w-full p-3 border rounded-md resize-none"
              rows={4}
              style={{
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.9)',
              }}
            />
          </div>
          {bulkRemoveReason.trim() && (
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1, fontStyle: 'italic' }}>
              This reason is for internal reference only and will not be sent to the candidates.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setShowBulkRemoveModal(false)} 
            disabled={isProcessing}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={processBulkRemove}
            variant="contained"
            disabled={!bulkRemoveReason.trim() || isProcessing}
            sx={{
              backgroundColor: 'rgba(244, 67, 54, 0.2)',
              color: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.3)',
                borderColor: 'rgba(244, 67, 54, 0.5)'
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isProcessing ? 'Removing...' : `Remove ${selectedCounsellors.size} Application(s)`}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
};

export default CounsellorManagement;
